import { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogBody, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ChevronDown, Check, X, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import type { Organisation, OrganisationFeatureFlags, SharedProps } from "@/types"

type Department = { code: string; name: string }
type CommuneName = { code: string; name: string; department_code: string }

const featureFlagLabels: Record<keyof OrganisationFeatureFlags, string> = {
  "developer-layout": "Layout développeur",
  "agregated-values": "Valeurs agrégées",
  "contacts-management": "Gestion des contacts",
  "use-duval-project": "Projet Duval",
  "prospects-export": "Export des prospects",
  "dispatch-prospects-users": "Dispatch prospects utilisateurs",
  "send-notification-postmail": "Notifications courrier",
  "merge-prospects": "Fusion des prospects",
  "cant-duplicate-parcels": "Interdire doublons parcelles",
  "kaptcher-explorer": "Explorateur Kaptcher",
  "study-mode": "Mode étude de faisabilité",
}

function withDefaults(flags?: OrganisationFeatureFlags): OrganisationFeatureFlags {
  const defaults = Object.fromEntries(
    Object.keys(featureFlagLabels).map((key) => [key, true]),
  ) as OrganisationFeatureFlags
  return { ...defaults, ...flags }
}

type PartnerOrg = { id: string; name: string }

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  organisation?: Organisation | null
  departments?: Department[]
  communeNames?: CommuneName[]
  allOrganisations?: PartnerOrg[]
}

export function OrganisationFormDialog({ open, onOpenChange, organisation, departments = [], communeNames = [], allOrganisations = [] }: Props) {
  const { current_user } = usePage<SharedProps>().props
  const isEdit = !!organisation
  const isSuperAdmin = current_user?.is_super_admin ?? false

  const [deptOpen, setDeptOpen] = useState(false)
  const [partnerOpen, setPartnerOpen] = useState(false)
  const [communeSearch, setCommuneSearch] = useState("")
  const [communeResults, setCommuneResults] = useState<CommuneName[]>([])
  const [communeSearching, setCommuneSearching] = useState(false)
  const [knownCommunes, setKnownCommunes] = useState<Map<string, CommuneName>>(new Map())
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>()

  // Initialize known communes from props
  useEffect(() => {
    if (communeNames.length > 0) {
      setKnownCommunes((prev) => {
        const next = new Map(prev)
        for (const c of communeNames) next.set(c.code, c)
        return next
      })
    }
  }, [communeNames])

  const searchCommunes = useCallback((query: string) => {
    setCommuneSearch(query)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    if (query.length < 2) {
      setCommuneResults([])
      return
    }
    setCommuneSearching(true)
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch(`/communes/search?q=${encodeURIComponent(query)}`)
        const data: CommuneName[] = await res.json()
        setCommuneResults(data)
      } finally {
        setCommuneSearching(false)
      }
    }, 300)
  }, [])

  const { data, setData, post, patch, processing, reset } = useForm({
    name: organisation?.name || "",
    organisation_type: organisation?.organisation_type || "promotor",
    is_active: organisation?.is_active ?? true,
    is_demo: organisation?.is_demo ?? false,
    site_url: organisation?.site_url || "",
    has_exclusivity: organisation?.has_exclusivity ?? true,
    has_lead_gen: organisation?.has_lead_gen ?? false,
    import_with_owners: organisation?.import_with_owners ?? false,
    feature_flags: withDefaults(organisation?.feature_flags),
    allowed_department_codes: organisation?.allowed_department_codes ?? [] as string[],
    allowed_commune_codes: organisation?.allowed_commune_codes ?? [] as string[],
    partner_organisation_ids: organisation?.partner_organisation_ids ?? [] as string[],
  })

  useEffect(() => {
    setData({
      name: organisation?.name || "",
      organisation_type: organisation?.organisation_type || "promotor",
      is_active: organisation?.is_active ?? true,
      is_demo: organisation?.is_demo ?? false,
      site_url: organisation?.site_url || "",
      has_exclusivity: organisation?.has_exclusivity ?? true,
      has_lead_gen: organisation?.has_lead_gen ?? false,
      import_with_owners: organisation?.import_with_owners ?? false,
      feature_flags: withDefaults(organisation?.feature_flags),
      allowed_department_codes: organisation?.allowed_department_codes ?? [],
      allowed_commune_codes: organisation?.allowed_commune_codes ?? [],
      partner_organisation_ids: organisation?.partner_organisation_ids ?? [],
    })
  }, [organisation])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const options = {
      onSuccess: () => {
        onOpenChange(false)
        reset()
      },
    }
    if (isEdit) {
      patch(`/organisations/${organisation!.id}`, options)
    } else {
      post("/organisations", options)
    }
  }

  const toggleDepartment = (code: string) => {
    const current = data.allowed_department_codes
    if (current.includes(code)) {
      setData("allowed_department_codes", current.filter((c) => c !== code))
    } else {
      setData("allowed_department_codes", [...current, code])
    }
  }

  const removeDepartment = (code: string) => {
    setData("allowed_department_codes", data.allowed_department_codes.filter((c) => c !== code))
  }

  const addCommune = (commune: CommuneName) => {
    if (!data.allowed_commune_codes.includes(commune.code)) {
      setData("allowed_commune_codes", [...data.allowed_commune_codes, commune.code])
      setKnownCommunes((prev) => new Map(prev).set(commune.code, commune))
    }
    setCommuneSearch("")
    setCommuneResults([])
  }

  const removeCommune = (code: string) => {
    setData("allowed_commune_codes", data.allowed_commune_codes.filter((c) => c !== code))
  }

  const togglePartnerOrg = (id: string) => {
    const current = data.partner_organisation_ids
    if (current.includes(id)) {
      setData("partner_organisation_ids", current.filter((i) => i !== id))
    } else {
      setData("partner_organisation_ids", [...current, id])
    }
  }

  const removePartnerOrg = (id: string) => {
    setData("partner_organisation_ids", data.partner_organisation_ids.filter((i) => i !== id))
  }

  const availablePartnerOrgs = useMemo(() => {
    return allOrganisations.filter((o) => o.id !== organisation?.id)
  }, [allOrganisations, organisation])

  const selectedDeptNames = useMemo(() => {
    const map = new Map(departments.map((d) => [d.code, d.name]))
    return data.allowed_department_codes.map((code) => ({ code, name: map.get(code) || code }))
  }, [data.allowed_department_codes, departments])

  const hasRestrictions = data.allowed_department_codes.length > 0 || data.allowed_commune_codes.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? `Modifier ${organisation!.name}` : "Nouvelle organisation"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dlg_org_name">Nom</Label>
              <Input id="dlg_org_name" value={data.name} onChange={(e) => setData("name", e.target.value)} required />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dlg_org_type">Type</Label>
              <Select
                value={data.organisation_type}
                onValueChange={(v) => setData("organisation_type", v as Organisation["organisation_type"])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="promotor">Promoteur</SelectItem>
                  <SelectItem value="reseller">Revendeur</SelectItem>
                  <SelectItem value="agency">Agence</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dlg_org_site_url">Site web</Label>
              <Input id="dlg_org_site_url" value={data.site_url} onChange={(e) => setData("site_url", e.target.value)} />
            </div>

            <div className="flex items-center gap-3">
              <Switch id="dlg_org_is_active" checked={data.is_active} onCheckedChange={(v) => setData("is_active", v)} />
              <Label htmlFor="dlg_org_is_active">Actif</Label>
            </div>

            <div className="flex items-center gap-3">
              <Switch id="dlg_org_is_demo" checked={data.is_demo} onCheckedChange={(v) => setData("is_demo", v)} />
              <Label htmlFor="dlg_org_is_demo">Démo</Label>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="dlg_org_has_exclusivity"
                checked={data.has_exclusivity}
                onCheckedChange={(v) => setData("has_exclusivity", v)}
              />
              <Label htmlFor="dlg_org_has_exclusivity">Exclusivité</Label>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="dlg_org_has_lead_gen"
                checked={data.has_lead_gen}
                onCheckedChange={(v) => setData("has_lead_gen", v)}
              />
              <Label htmlFor="dlg_org_has_lead_gen">Génération de leads</Label>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="dlg_org_import_with_owners"
                checked={data.import_with_owners}
                onCheckedChange={(v) => setData("import_with_owners", v)}
              />
              <Label htmlFor="dlg_org_import_with_owners">Importer avec les propriétaires (données cadastrales)</Label>
            </div>

            {isSuperAdmin && (
              <>
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium w-full">
                    <ChevronDown className="size-4 transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                    Feature Flags
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-3 pt-3">
                    {(Object.entries(featureFlagLabels) as [keyof OrganisationFeatureFlags, string][]).map(
                      ([key, label]) => (
                        <div key={key} className="flex items-center gap-3">
                          <Switch
                            id={`dlg_ff_${key}`}
                            checked={data.feature_flags?.[key] ?? true}
                            onCheckedChange={(v) =>
                              setData("feature_flags", { ...data.feature_flags, [key]: v })
                            }
                          />
                          <Label htmlFor={`dlg_ff_${key}`}>{label}</Label>
                        </div>
                      ),
                    )}
                  </CollapsibleContent>
                </Collapsible>

                <Collapsible>
                  <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium w-full">
                    <ChevronDown className="size-4 transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                    Communes autorisées
                    {!hasRestrictions && (
                      <span className="text-xs text-muted-foreground font-normal ml-auto">Toutes</span>
                    )}
                    {hasRestrictions && (
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {data.allowed_department_codes.length} dép.
                      </Badge>
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-3 pt-3">
                    <div className="space-y-2">
                      <Label>Départements</Label>
                      <Popover open={deptOpen} onOpenChange={setDeptOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                            {data.allowed_department_codes.length > 0
                              ? `${data.allowed_department_codes.length} département(s) sélectionné(s)`
                              : "Sélectionner des départements..."}
                            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[300px] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Rechercher un département..." />
                            <CommandList>
                              <CommandEmpty>Aucun département trouvé</CommandEmpty>
                              <CommandGroup>
                                {departments.map((dept) => (
                                  <CommandItem
                                    key={dept.code}
                                    value={`${dept.code} ${dept.name}`}
                                    onSelect={() => toggleDepartment(dept.code)}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 size-4",
                                        data.allowed_department_codes.includes(dept.code) ? "opacity-100" : "opacity-0",
                                      )}
                                    />
                                    <span className="text-muted-foreground mr-1.5">{dept.code}</span>
                                    {dept.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>

                      {selectedDeptNames.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {selectedDeptNames.map(({ code, name }) => (
                            <Badge key={code} variant="secondary" className="gap-1">
                              {code} - {name}
                              <button
                                type="button"
                                onClick={() => removeDepartment(code)}
                                className="ml-0.5 rounded-full hover:bg-muted-foreground/20"
                              >
                                <X className="size-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Communes individuelles</Label>
                      <div className="relative">
                        <Input
                          placeholder="Rechercher une commune..."
                          value={communeSearch}
                          onChange={(e) => searchCommunes(e.target.value)}
                        />
                        {communeResults.length > 0 && (
                          <div className="absolute top-full left-0 z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-48 overflow-y-auto">
                            {communeResults
                              .filter((c) => !data.allowed_commune_codes.includes(c.code))
                              .map((commune) => (
                                <button
                                  key={commune.code}
                                  type="button"
                                  className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent cursor-pointer"
                                  onClick={() => addCommune(commune)}
                                >
                                  <span className="text-muted-foreground">{commune.code}</span>
                                  {commune.name}
                                  <span className="ml-auto text-xs text-muted-foreground">({commune.department_code})</span>
                                </button>
                              ))}
                            {communeResults.filter((c) => !data.allowed_commune_codes.includes(c.code)).length === 0 && (
                              <p className="px-3 py-2 text-sm text-muted-foreground">Déjà sélectionnées</p>
                            )}
                          </div>
                        )}
                        {communeSearching && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">...</span>
                        )}
                      </div>

                      {data.allowed_commune_codes.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {data.allowed_commune_codes.map((code) => {
                            const commune = knownCommunes.get(code)
                            return (
                              <Badge key={code} variant="secondary" className="gap-1">
                                {commune ? `${commune.name} (${commune.department_code})` : code}
                                <button
                                  type="button"
                                  onClick={() => removeCommune(code)}
                                  className="ml-0.5 rounded-full hover:bg-muted-foreground/20"
                                >
                                  <X className="size-3" />
                                </button>
                              </Badge>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    {!hasRestrictions && (
                      <p className="text-xs text-muted-foreground">
                        Aucune restriction — l'organisation a accès à toutes les communes.
                      </p>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                <Collapsible>
                  <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium w-full">
                    <ChevronDown className="size-4 transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                    Organisations liées
                    {data.partner_organisation_ids.length > 0 && (
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {data.partner_organisation_ids.length}
                      </Badge>
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-3 pt-3">
                    <div className="space-y-2">
                      <Popover open={partnerOpen} onOpenChange={setPartnerOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                            {data.partner_organisation_ids.length > 0
                              ? `${data.partner_organisation_ids.length} organisation(s) liée(s)`
                              : "Sélectionner des organisations..."}
                            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[300px] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Rechercher une organisation..." />
                            <CommandList>
                              <CommandEmpty>Aucune organisation trouvée</CommandEmpty>
                              <CommandGroup>
                                {availablePartnerOrgs.map((org) => (
                                  <CommandItem
                                    key={org.id}
                                    value={org.name}
                                    onSelect={() => togglePartnerOrg(org.id)}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 size-4",
                                        data.partner_organisation_ids.includes(org.id) ? "opacity-100" : "opacity-0",
                                      )}
                                    />
                                    {org.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>

                      {data.partner_organisation_ids.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {data.partner_organisation_ids.map((id) => {
                            const org = allOrganisations.find((o) => o.id === id)
                            return (
                              <Badge key={id} variant="secondary" className="gap-1">
                                {org?.name || id}
                                <button
                                  type="button"
                                  onClick={() => removePartnerOrg(id)}
                                  className="ml-0.5 rounded-full hover:bg-muted-foreground/20"
                                >
                                  <X className="size-3" />
                                </button>
                              </Badge>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={processing}>
              {isEdit ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
